import { ICondition, IEditorNode } from './editorModel';
import {
  IMigrationMark,
  IRichParagraph,
  IRichRun,
  paragraphsOfNode,
  plainTextOfParagraph,
} from './richTextModel';

/**
 * Leseansicht eines Abschnitts (#1245, Teil 43).
 *
 * Der Editor zeigte jeden Knoten als eigene Karte, einzeilig abgeschnitten. Ein geklonter Satz
 * mit zwei Bedingungen darin — »… versichern wir / versichere ich an Eides Statt, dass uns / mir
 * nichts bekannt ist …« — zerfiel in sieben gleichrangige Karten; lesen ließ er sich nicht.
 *
 * Hier wird aus der Knotenfolge wieder ein Lesefluss: Absätze entstehen dort, wo das Modell ein
 * Absatzende hat (ein Absatz mit `continues` läuft im nächsten Knoten weiter), Datenfelder und
 * kurze Bedingungen stehen im Satz, Bedingungen mit eigenen Absätzen, Tabellen oder Unterknoten
 * werden Container. Reine Anzeige: an Knoten, Reihenfolge oder Bedeutung ändert sich nichts,
 * und nichts wird ausgewertet.
 */

/** Weg zu einem Knoten: Index im Abschnitt, dann im Zweig (`nodes`) usw. */
export type NodePath = number[];

export interface IAlternative {
  path: NodePath;
  condition: ICondition | null;
  text: string;
}

export type ReadPiece =
  | {
      kind: 'text';
      text: string;
      bold?: boolean;
      italic?: boolean;
      underline?: boolean;
    }
  | { kind: 'break' }
  | { kind: 'field'; selector: string; label?: string | null; path: NodePath }
  | { kind: 'choice'; path: NodePath; alternatives: IAlternative[] }
  | { kind: 'open'; path: NodePath; mark: IMigrationMark }
  | { kind: 'marker'; path: NodePath; node: IEditorNode };

export type ReadBlock =
  | {
      kind: 'paragraph';
      path: NodePath;
      paragraph: IRichParagraph;
      pieces: ReadPiece[];
      /** Welcher Absatz des Knotens hier beginnt — damit der Editor dort den Cursor setzt. */
      paragraphIndex?: number;
    }
  | {
      kind: 'condition';
      path: NodePath;
      condition: ICondition | null;
      blocks: ReadBlock[];
    }
  | { kind: 'table'; path: NodePath; rows: ReadBlock[][][] }
  | { kind: 'element'; path: NodePath; node: IEditorNode }
  /** Das Element, das gerade an Ort und Stelle bearbeitet wird (#1245, Teil 48). */
  | { kind: 'editor'; path: NodePath; node: IEditorNode };

/** Knoten, die keinen Text schreiben, aber eine Stelle im Ablauf haben. */
const INLINE_MARKERS = new Set(['question', 'input', 'migration_required']);

const piecesOfRuns = (runs: IRichRun[], path: NodePath): ReadPiece[] =>
  (runs ?? []).flatMap((run): ReadPiece[] => {
    if (run.migration) {
      return [{ kind: 'open', path, mark: run.migration }];
    }
    if (run.choice) {
      return [
        {
          kind: 'choice',
          path,
          alternatives: run.choice.alternatives.map(alternative => ({
            path,
            condition: alternative.condition,
            text: alternative.text,
          })),
        },
      ];
    }
    if (run.selector) {
      return [
        { kind: 'field', selector: run.selector, label: run.label, path },
      ];
    }
    if (run.lineBreak) {
      return [{ kind: 'break' }];
    }
    if (!run.text) {
      return [];
    }
    return [
      {
        kind: 'text',
        text: run.text,
        bold: run.bold,
        italic: run.italic,
        underline: run.underline,
      },
    ];
  });

/** Beide Bedingungen fragen dasselbe — dann sind es Alternativen einer Stelle im Satz. */
const sameSubject = (
  a: ICondition | null | undefined,
  b: ICondition | null | undefined
): boolean => {
  if (!a || !b || a.left.type !== b.left.type) {
    return false;
  }
  if (a.left.type === 'selector') {
    return Boolean(a.left.selector) && a.left.selector === b.left.selector;
  }
  if (a.left.type === 'question') {
    return (
      Boolean(a.left.questionId) && a.left.questionId === b.left.questionId
    );
  }
  return false;
};

/** Fortlaufender Text ohne Absatzende: ein Knoten, der mitten im Satz steht. */
const flowsInline = (node: IEditorNode): boolean => {
  if (node.type === 'text') {
    return paragraphsOfNode(node).every(paragraph => paragraph.continues);
  }
  if (node.type === 'data_field') {
    return true;
  }
  if (node.type === 'conditional_text') {
    const children = node.nodes ?? [];
    if (children.length > 0) {
      return children.every(flowsInline);
    }
    const paragraphs = paragraphsOfNode(node);
    return paragraphs.length === 1 && Boolean(paragraphs[0].continues);
  }
  return false;
};

/**
 * Steht diese Bedingung im Satz? Ein Zweig aus genau einem Absatz, der einen laufenden Absatz
 * fortsetzt oder selbst weiterläuft — oder ein zusammengesetzter Zweig (Word-IF in Word-IF), der
 * nur aus solchem fortlaufenden Text und weiteren Inline-Bedingungen besteht. Alles andere —
 * mehrere Absätze, Tabellen, Fragen, Verweise — ist ein Block.
 */
export const isInlineCondition = (
  node: IEditorNode,
  paragraphOpen: boolean
): boolean => {
  if (node.type !== 'conditional_text') {
    return false;
  }
  const children = node.nodes ?? [];
  if (children.length > 0) {
    return children.every(flowsInline);
  }
  const paragraphs = paragraphsOfNode(node);
  return (
    paragraphs.length === 1 &&
    (paragraphOpen || Boolean(paragraphs[0].continues))
  );
};

/** Der Zweigtext zum Lesen — verschachtelte Alternativen als »[a | b]«. */
export const inlineText = (node: IEditorNode): string => {
  const children = node.nodes ?? [];
  if (node.type !== 'conditional_text' || children.length === 0) {
    return paragraphsOfNode(node).map(plainTextOfParagraph).join(' ');
  }
  const out: string[] = [];
  let group: string[] = [];
  let groupCondition: ICondition | null | undefined = null;
  const flush = () => {
    if (group.length > 0) {
      out.push(`[${group.join(' | ')}]`);
    }
    group = [];
    groupCondition = null;
  };
  children.forEach(child => {
    if (child.type === 'conditional_text') {
      if (!(group.length > 0 && sameSubject(groupCondition, child.condition))) {
        flush();
        groupCondition = child.condition;
      }
      group.push(inlineText(child).trim() || '—');
      return;
    }
    flush();
    out.push(inlineText(child));
  });
  flush();
  return out.join('');
};

const samePath = (a: NodePath, b: NodePath | null | undefined): boolean =>
  Boolean(b) &&
  a.length === b!.length &&
  a.every((value, index) => value === b![index]);

/** Welche Elemente sich direkt in der Leseansicht bearbeiten lassen. */
export const editableInPlace = (node: IEditorNode | null): boolean =>
  node?.type === 'text';

export const readingFlow = (
  nodes: IEditorNode[],
  base: NodePath = [],
  editing: NodePath | null = null
): ReadBlock[] => {
  const blocks: ReadBlock[] = [];
  let open: Extract<ReadBlock, { kind: 'paragraph' }> | null = null;

  const start = (
    paragraph: IRichParagraph,
    path: NodePath,
    paragraphIndex?: number
  ): Extract<ReadBlock, { kind: 'paragraph' }> => {
    const created: Extract<ReadBlock, { kind: 'paragraph' }> = {
      kind: 'paragraph',
      path,
      paragraph,
      pieces: [],
      ...(paragraphIndex === undefined ? {} : { paragraphIndex }),
    };
    blocks.push(created);
    open = created;
    return created;
  };

  const paragraphsInto = (paragraphs: IRichParagraph[], path: NodePath) => {
    paragraphs.forEach((paragraph, paragraphIndex) => {
      const target = open ?? start(paragraph, path, paragraphIndex);
      target.pieces.push(...piecesOfRuns(paragraph.runs, path));
      if (!paragraph.continues) {
        open = null;
      }
    });
  };

  nodes.forEach((node, index) => {
    const path = [...base, index];
    // Das bearbeitete Element steht als Ganzes da: ein eigener Block, der Lesefluss bricht davor
    // und danach. Ein Satz, der im nächsten Element weitergeht, setzt dort neu an.
    if (samePath(path, editing) && editableInPlace(node)) {
      open = null;
      blocks.push({ kind: 'editor', path, node });
      return;
    }
    switch (node.type) {
      case 'text':
        paragraphsInto(paragraphsOfNode(node), path);
        return;
      case 'data_field':
        (open ?? start({ runs: [] }, path)).pieces.push({
          kind: 'field',
          selector: node.selector ?? '',
          label: node.label,
          path,
        });
        return;
      case 'conditional_text': {
        if (isInlineCondition(node, open !== null)) {
          const composite = (node.nodes ?? []).length > 0;
          const paragraph = composite
            ? { runs: [], continues: true }
            : paragraphsOfNode(node)[0];
          const target = open ?? start(paragraph, path);
          const alternative: IAlternative = {
            path,
            condition: node.condition ?? null,
            text: composite
              ? inlineText(node)
              : plainTextOfParagraph(paragraph),
          };
          const last = target.pieces[target.pieces.length - 1];
          const lastAlternative =
            last?.kind === 'choice'
              ? last.alternatives[last.alternatives.length - 1]
              : null;
          // Nur direkt aufeinanderfolgende Zweige zur selben Sache: »wir | ich«.
          if (
            last?.kind === 'choice' &&
            lastAlternative &&
            lastAlternative.path[lastAlternative.path.length - 1] ===
              index - 1 &&
            sameSubject(lastAlternative.condition, node.condition)
          ) {
            last.alternatives.push(alternative);
          } else {
            target.pieces.push({
              kind: 'choice',
              path,
              alternatives: [alternative],
            });
          }
          if (!paragraph.continues) {
            open = null;
          }
          return;
        }
        open = null;
        blocks.push({
          kind: 'condition',
          path,
          condition: node.condition ?? null,
          blocks:
            (node.nodes ?? []).length > 0
              ? readingFlow(node.nodes ?? [], path, editing)
              : paragraphBlocks(paragraphsOfNode(node), path),
        });
        return;
      }
      case 'table':
        open = null;
        blocks.push({
          kind: 'table',
          path,
          rows: (node.rows ?? []).map(row =>
            row.cells.map(cell => paragraphBlocks(cell.paragraphs ?? [], path))
          ),
        });
        return;
      default:
        if (open && INLINE_MARKERS.has(node.type)) {
          (open as Extract<ReadBlock, { kind: 'paragraph' }>).pieces.push({
            kind: 'marker',
            path,
            node,
          });
          return;
        }
        open = null;
        blocks.push({ kind: 'element', path, node });
    }
  });
  return blocks;
};

/** Absätze ohne Knotenstruktur (Zelle, einfacher Bedingungszweig) — derselbe Absatzfluss. */
export const paragraphBlocks = (
  paragraphs: IRichParagraph[],
  path: NodePath
): ReadBlock[] =>
  readingFlow([{ type: 'text', paragraphs }], []).map(block =>
    retarget(block, path)
  );

const retarget = (block: ReadBlock, path: NodePath): ReadBlock => {
  if (block.kind === 'paragraph') {
    return {
      ...block,
      path,
      pieces: block.pieces.map(piece =>
        'path' in piece ? { ...piece, path } : piece
      ) as ReadPiece[],
    };
  }
  return { ...block, path };
};

/** Der Knoten an einem Weg — auch in Zweigen. */
export const nodeAt = (
  nodes: IEditorNode[],
  path: NodePath
): IEditorNode | null => {
  let list = nodes;
  let found: IEditorNode | null = null;
  for (const index of path) {
    found = list[index] ?? null;
    if (!found) {
      return null;
    }
    list = found.nodes ?? [];
  }
  return found;
};

/** Den Knoten an einem Weg ersetzen — auch in Zweigen; alles andere bleibt, wie es ist. */
export const replaceNodeAt = (
  nodes: IEditorNode[],
  path: NodePath,
  next: IEditorNode
): IEditorNode[] => {
  if (path.length === 0 || !nodes[path[0]]) {
    return nodes;
  }
  const [index, ...rest] = path;
  return nodes.map((node, position) => {
    if (position !== index) {
      return node;
    }
    return rest.length === 0
      ? next
      : { ...node, nodes: replaceNodeAt(node.nodes ?? [], rest, next) };
  });
};

/** Wie tief der Lesefluss verschachtelt ist — für Tests und die Einrückung. */
export const depthOf = (blocks: ReadBlock[]): number =>
  blocks.reduce(
    (deepest, block) =>
      block.kind === 'condition'
        ? Math.max(deepest, 1 + depthOf(block.blocks))
        : deepest,
    0
  );
